/**
 * HOOK: useMutationAudit
 *
 * Extracts the mutation audit trail concern from App.jsx — 8 useState pairs,
 * 3 methods, and 2 derived values.
 *
 * Flat convivencia-friendly return: all variables are top-level named
 * values so App.jsx can destructure them directly alongside current
 * inline states without renaming.
 */

import { useState, useMemo, useEffect, useCallback } from 'react'

// ── Constants (mirror App.jsx values) ──────────────────────────────

const MIN_REASON_DETAIL_LEN = 20
const WEAK_REASON_PATTERNS = [
  /^ok$/i,
  /^cambio$/i,
  /^ajuste$/i,
  /^corregido$/i,
  /^porque\s*s[ií]$/i,
  /^manual$/i,
]

// ── Internal pure helpers ──────────────────────────────────────────

const normalizeReasonDetail = (value) => String(value || '').replace(/\s+/g, ' ').trim()

function isWeakReasonDetail(value) {
  const normalized = normalizeReasonDetail(value)
  if (normalized.length < MIN_REASON_DETAIL_LEN) return true
  return WEAK_REASON_PATTERNS.some((rx) => rx.test(normalized))
}

function buildMutationsCanonicalJson(input) {
  const rows = (Array.isArray(input) ? input : []).map((m) => ({
    id: String(m?.id || ''),
    rowId: String(m?.rowId || ''),
    periodo: String(m?.periodo || ''),
    field: String(m?.field || ''),
    originalValue: m?.originalValue ?? null,
    newValue: m?.newValue ?? null,
    reasonCode: String(m?.reasonCode || ''),
    reasonDetail: String(m?.reasonDetail || ''),
    timestamp: String(m?.timestamp || ''),
    sourcePage: Number(m?.sourcePage || 0),
  }))
    .sort((a, b) => `${a.timestamp}|${a.id}`.localeCompare(`${b.timestamp}|${b.id}`))
  return JSON.stringify(rows)
}

async function sha256Hex(raw) {
  if (!globalThis?.crypto?.subtle) return 'hash_no_disponible'
  const bytes = new TextEncoder().encode(String(raw || ''))
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

// ── Hook ──────────────────────────────────────────────────────────

export default function useMutationAudit({
  userToken,
  caseId,
  expediente,
}) {
  const [mutationsLog, setMutationsLog] = useState([])
  const [showMutationsAccordion, setShowMutationsAccordion] = useState(false)
  const [showMutationReasonModal, setShowMutationReasonModal] = useState(false)
  const [pendingMutationAction, setPendingMutationAction] = useState(null)
  const [mutationReasonDraft, setMutationReasonDraft] = useState({ reasonCode: 'ajuste_manual_perito', reasonDetail: '' })
  const [mutationsIntegrityHash, setMutationsIntegrityHash] = useState('')
  const [copyHashFeedback, setCopyHashFeedback] = useState('')
  const [allowLitigioArtesanalBypass, setAllowLitigioArtesanalBypass] = useState(false)

  const mutationReasonWeak = isWeakReasonDetail(mutationReasonDraft.reasonDetail)

  const mutationsQualityAudit = useMemo(() => {
    const entries = (Array.isArray(mutationsLog) ? mutationsLog : []).filter((m) =>
      isWeakReasonDetail(m?.reasonDetail),
    )
    return {
      hasWeakJustifications: entries.length > 0,
      invalidCount: entries.length,
      invalidEntries: entries,
    }
  }, [mutationsLog])

  const buildMutationId = useCallback(() => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, [])

  // ── Integrity hash effect ────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    const compute = async () => {
      const canonical = buildMutationsCanonicalJson(mutationsLog)
      const hash = await sha256Hex(canonical)
      if (!cancelled) setMutationsIntegrityHash(hash)
    }
    compute()
    return () => {
      cancelled = true
    }
  }, [mutationsLog])

  // ── Load from server effect ─────────────────────────────────────
  useEffect(() => {
    const loadCaseAudit = async () => {
      if (!userToken || !caseId) return
      try {
        const res = await fetch(`/api/user/cases/${encodeURIComponent(caseId)}`, {
          headers: { Authorization: `Bearer ${userToken}` },
        })
        const data = await res.json()
        const serverLog = Array.isArray(data?.mutationsLog)
          ? data.mutationsLog
          : Array.isArray(data?.expediente?.mutationsLog)
            ? data.expediente.mutationsLog
            : []
        setMutationsLog(serverLog)
      } catch {
        // ignore and keep local audit log
      }
    }
    loadCaseAudit()
  }, [userToken, caseId])

  // ── syncCaseAudit ───────────────────────────────────────────────
  const syncCaseAudit = useCallback(async (nextMutations) => {
    if (!userToken || !caseId) return
    try {
      await fetch(`/api/user/cases/${encodeURIComponent(caseId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          expediente,
          mutationsLog: Array.isArray(nextMutations) ? nextMutations : [],
        }),
      })
    } catch {
      // keep local state if backend sync fails
    }
  }, [userToken, caseId, expediente])

  // ── openMutationReasonModal ─────────────────────────────────────
  const openMutationReasonModal = useCallback((payload) => {
    setPendingMutationAction(payload)
    setMutationReasonDraft({ reasonCode: 'ajuste_manual_perito', reasonDetail: '' })
    setShowMutationReasonModal(true)
  }, [])

  // ── commitMutationAction ────────────────────────────────────────
  const commitMutationAction = useCallback(async () => {
    if (!pendingMutationAction) return
    const detail = normalizeReasonDetail(mutationReasonDraft.reasonDetail)
    if (isWeakReasonDetail(detail)) {
      alert(`La justificacion debe ser descriptiva y tener al menos ${MIN_REASON_DETAIL_LEN} caracteres.`)
      return
    }
    pendingMutationAction.apply()
    const entry = {
      id: buildMutationId(),
      rowId: String(pendingMutationAction.rowId || ''),
      periodo: String(pendingMutationAction.periodo || ''),
      field: String(pendingMutationAction.field || 'estadoComputo'),
      originalValue: pendingMutationAction.originalValue,
      newValue: pendingMutationAction.newValue,
      reasonCode: String(mutationReasonDraft.reasonCode || 'ajuste_manual_perito'),
      reasonDetail: detail,
      timestamp: new Date().toISOString(),
      sourcePage: Number(pendingMutationAction.sourcePage || 0),
    }
    const next = [...mutationsLog, entry]
    setMutationsLog(next)
    await syncCaseAudit(next)
    setShowMutationReasonModal(false)
    setPendingMutationAction(null)
  }, [pendingMutationAction, mutationReasonDraft, mutationsLog, buildMutationId, syncCaseAudit])

  // ── registrarBypassLitigioArtesanal ─────────────────────────────
  const registrarBypassLitigioArtesanal = useCallback(async () => {
    const entry = {
      id: buildMutationId(),
      rowId: 'dictamen-bypass-no-perjuicio',
      periodo: new Date().toISOString().slice(0, 7),
      field: 'estadoComputo',
      originalValue: 'juicio_inviable_bloqueado',
      newValue: 'juicio_inviable_bypass_litigio_artesanal',
      reasonCode: 'ajuste_manual_perito',
      reasonDetail: 'Bypass de inviabilidad por estrategia de litigio artesanal',
      timestamp: new Date().toISOString(),
      sourcePage: 0,
    }
    const next = [...mutationsLog, entry]
    setMutationsLog(next)
    setAllowLitigioArtesanalBypass(true)
    await syncCaseAudit(next)
  }, [buildMutationId, mutationsLog, syncCaseAudit])

  // ── resetMutations ──────────────────────────────────────────────
  const resetMutations = useCallback(() => {
    setMutationsLog([])
    setShowMutationsAccordion(false)
    setShowMutationReasonModal(false)
    setPendingMutationAction(null)
    setMutationReasonDraft({ reasonCode: 'ajuste_manual_perito', reasonDetail: '' })
    setMutationsIntegrityHash('')
    setCopyHashFeedback('')
    setAllowLitigioArtesanalBypass(false)
  }, [])

  return {
    // State
    mutationsLog,
    setMutationsLog,
    showMutationsAccordion,
    setShowMutationsAccordion,
    showMutationReasonModal,
    setShowMutationReasonModal,
    pendingMutationAction,
    setPendingMutationAction,
    mutationReasonDraft,
    setMutationReasonDraft,
    mutationsIntegrityHash,
    setMutationsIntegrityHash,
    copyHashFeedback,
    setCopyHashFeedback,
    allowLitigioArtesanalBypass,
    setAllowLitigioArtesanalBypass,

    // Computed
    mutationsQualityAudit,
    mutationReasonWeak,

    // Actions
    openMutationReasonModal,
    commitMutationAction,
    registrarBypassLitigioArtesanal,
    resetMutations,
    syncCaseAudit,
  }
}
